import { useContext, useMemo } from 'react';

import AppContext from '@/contexts/appContext';
import {
  BillingTimelineTarget,
  buildBillingTimelineUrl,
  buildVobListUrl
} from '@/utils/insuranceDeepLinks';
import { BILLING_SUPER_ADMIN_GROUP, isInGroup, VOB_EDITORS_GROUP } from '@/utils/userPermission';

export interface InsuranceDeepLinks {
  billingTimelineUrl: (policyId: string, target?: BillingTimelineTarget) => string | null;
  memberBillingTimelineUrl: (target?: BillingTimelineTarget) => string | null;
  vobListUrl: (policyId: string) => string | null;
  memberVobListUrl: () => string | null;
}

export const useInsuranceDeepLinks = (personId: string): InsuranceDeepLinks => {
  const { permissions } = useContext(AppContext);

  const canOpenTimeline = isInGroup(permissions, BILLING_SUPER_ADMIN_GROUP);
  const canOpenVobList = isInGroup(permissions, VOB_EDITORS_GROUP);

  return useMemo(
    () => ({
      billingTimelineUrl: (policyId, target = {}) =>
        canOpenTimeline ? buildBillingTimelineUrl({ personId, policyId, ...target }) : null,
      memberBillingTimelineUrl: (target = {}) =>
        canOpenTimeline ? buildBillingTimelineUrl({ personId, ...target }) : null,
      vobListUrl: (policyId) =>
        canOpenVobList ? buildVobListUrl({ policyId, memberId: personId }) : null,
      memberVobListUrl: () => (canOpenVobList ? buildVobListUrl({ memberId: personId }) : null)
    }),
    [canOpenTimeline, canOpenVobList, personId]
  );
};

export default useInsuranceDeepLinks;
